/**
 * <eyewear-product>
 *
 * Generic UI-state controller for the eyewear "Options & More" configurator.
 * It has no built-in knowledge of any specific group or choice (no
 * "Purchase Type" / "Vision Type" hardcoding) — it discovers an arbitrary
 * number of option groups and choices from data attributes, so the same
 * engine drives both:
 *   - the legacy hardcoded markup (snippets/eyewear-options.liquid), and
 *   - the metaobject-driven dynamic markup (snippets/eyewear-options-dynamic.liquid)
 * with no code change required as groups/choices are added or removed.
 *
 * Markup contract (produced by both snippets above):
 * - each option group is an element with [data-option-group], carrying
 *   data-depends-on="handle,handle" (comma-separated choice handles from
 *   ANY group in the form; empty/absent = always visible) and an optional
 *   data-default-choice="handle" (the choice to select when this group is
 *   visible and nothing in it is currently selected; absent = never
 *   auto-select anything in this group)
 * - each selectable choice is a radio input with [data-choice-input] whose
 *   `value` is that choice's stable handle
 *
 * Visibility + default-selection algorithm: whenever any choice changes,
 * recompute a fixed point over every group:
 *   - a group that is NOT visible has its own selection cleared (if any)
 *   - a group that IS visible and currently has no selection gets its
 *     Default Choice selected (if one is configured and matches an actual
 *     choice in that group)
 * Either action can change what's selected, which can in turn change
 * downstream groups' visibility/defaults, so this repeats until a pass
 * makes no more changes. This is what lets a default cascade through (or
 * a clear cascade through) a chain of any depth without a hardcoded
 * number of levels, and it converges deterministically: each group can
 * only move from unselected to selected, or from selected to unselected,
 * per direction dictated by its own visibility, so the loop is bounded by
 * the number of groups and cannot oscillate.
 *
 * Also handles two small, additive extensions on top of the same
 * per-group "what's currently checked" data this engine already computes:
 *   - specification panels: an element with [data-spec-panel-for="handle"]
 *     inside a group is shown only while that exact choice is checked in
 *     that group (and the group itself is visible), hidden otherwise —
 *     same reasoning as group visibility, just choice-scoped instead of
 *     group-scoped.
 *   - price summary: choice inputs may carry data-price-cents (an
 *     integer, shop's smallest currency unit). Only choices that are both
 *     selected AND currently visible ever contribute — which is already
 *     guaranteed by the fixed-point pass above (anything that would be
 *     hidden has already had its selection cleared before totals are
 *     computed), so summing "whatever is checked at the end" is correct
 *     with no extra filtering. This is storefront display only; it does
 *     not affect Shopify checkout pricing.
 *
 * Responsibility in this phase: UI state only (visibility + selection).
 * Nothing here writes to the product form or submits to the cart.
 */
if (!customElements.get('eyewear-product')) {
  customElements.define(
    'eyewear-product',
    class EyewearProduct extends HTMLElement {
      constructor() {
        super();
        this.state = { selections: {}, optionsAmountCents: 0 };
      }

      connectedCallback() {
        this.groups = Array.from(this.querySelectorAll('[data-option-group]')).map((element) => ({
          element,
          handle: element.dataset.groupHandle || '',
          dependsOn: (element.dataset.dependsOn || '')
            .split(',')
            .map((handle) => handle.trim())
            .filter(Boolean),
          defaultChoice: element.dataset.defaultChoice || null,
          inputs: Array.from(element.querySelectorAll('[data-choice-input]')),
          specPanels: Array.from(element.querySelectorAll('[data-spec-panel-for]')),
        }));

        this.priceSummary = this.querySelector('[data-price-summary]');
        this.optionsAmountEl = this.querySelector('[data-options-amount]');
        this.finalTotalEl = this.querySelector('[data-final-total]');

        this.addEventListener('change', this.onChoiceChange.bind(this));
        this.updateVisibility();
      }

      onChoiceChange(event) {
        if (!event.target.matches('[data-choice-input]')) return;
        this.updateVisibility();
        this.dispatchStateChange();
      }

      getSelectedHandles() {
        const selected = new Set();
        this.groups.forEach((group) => {
          const checked = group.inputs.find((input) => input.checked);
          if (checked) selected.add(checked.value);
        });
        return selected;
      }

      isVisible(group, selectedHandles) {
        if (group.dependsOn.length === 0) return true;
        return group.dependsOn.some((handle) => selectedHandles.has(handle));
      }

      updateVisibility() {
        if (!this.groups) return;

        // Fixed-point pass: a group becoming hidden clears its selection;
        // a group becoming visible with nothing selected applies its
        // Default Choice. Either can change what's selected, which can
        // affect further groups, so repeat until a pass changes nothing
        // (bounded by group count — always terminates).
        let changed = true;
        while (changed) {
          changed = false;
          const selectedHandles = this.getSelectedHandles();

          this.groups.forEach((group) => {
            const visible = this.isVisible(group, selectedHandles);
            const checkedInput = group.inputs.find((input) => input.checked);

            if (!visible) {
              if (checkedInput) {
                checkedInput.checked = false;
                changed = true;
              }
              return;
            }

            if (!checkedInput && group.defaultChoice) {
              const defaultInput = group.inputs.find((input) => input.value === group.defaultChoice);
              if (defaultInput) {
                defaultInput.checked = true;
                changed = true;
              }
            }
          });
        }

        const finalSelectedHandles = this.getSelectedHandles();
        this.state.selections = {};
        let optionsAmountCents = 0;

        this.groups.forEach((group) => {
          const visible = this.isVisible(group, finalSelectedHandles);
          group.element.hidden = !visible;
          group.element.setAttribute('aria-hidden', String(!visible));
          group.inputs.forEach((input) => {
            input.disabled = !visible;
          });

          const checked = group.inputs.find((input) => input.checked);

          group.specPanels.forEach((panel) => {
            panel.hidden = !(visible && checked && panel.dataset.specPanelFor === checked.value);
          });

          if (checked) {
            this.state.selections[group.handle] = checked.value;
            const price = parseInt(checked.dataset.priceCents || '0', 10);
            if (!Number.isNaN(price)) optionsAmountCents += price;
          }
        });

        this.state.optionsAmountCents = optionsAmountCents;
        this.updatePriceSummary();
      }

      updatePriceSummary() {
        if (!this.priceSummary) return;

        const basePriceCents = parseInt(this.priceSummary.dataset.basePriceCents || '0', 10) || 0;
        const currencyCode = this.priceSummary.dataset.currencyCode || undefined;
        const finalTotalCents = basePriceCents + this.state.optionsAmountCents;

        if (this.optionsAmountEl) {
          this.optionsAmountEl.textContent = this.formatMoney(this.state.optionsAmountCents, currencyCode);
        }
        if (this.finalTotalEl) {
          this.finalTotalEl.textContent = this.formatMoney(finalTotalCents, currencyCode);
        }
      }

      formatMoney(minorUnits, currencyCode) {
        const amount = minorUnits / 100;
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode || 'USD',
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (error) {
          return amount.toFixed(2);
        }
      }

      dispatchStateChange() {
        this.dispatchEvent(
          new CustomEvent('eyewear:state-change', {
            bubbles: true,
            detail: {
              selections: { ...this.state.selections },
              optionsAmountCents: this.state.optionsAmountCents,
            },
          })
        );
      }
    }
  );
}
